import { useMutation } from '@tanstack/react-query';
import type { MutationFunction, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

export interface IGatedMutationOptions<TData, TError, TVariables, TContext>
  extends UseMutationOptions<TData, TError, TVariables, TContext> {
  isInstructionCurrent: (variables: TVariables) => boolean;
  mutationFn: MutationFunction<TData, TVariables>;
}

export type IGatedMutationResult<TData, TError, TVariables, TContext> = Omit<
  UseMutationResult<TData, TError, TVariables, TContext>,
  'mutateAsync'
>;

export const useGatedMutation = <TData, TError, TVariables, TContext>({
  isInstructionCurrent,
  ...options
}: IGatedMutationOptions<TData, TError, TVariables, TContext>): IGatedMutationResult<
  TData,
  TError,
  TVariables,
  TContext
> => {
  const { mutate, ...mutation } = useMutation<TData, TError, TVariables, TContext>(options);

  return {
    ...mutation,
    mutate: (variables, mutateOptions) => {
      if (!isInstructionCurrent(variables)) {
        mutateOptions?.onSettled?.(undefined, null, variables, undefined);
        return;
      }

      mutate(variables, mutateOptions);
    },
  };
};
